import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, FormikProps } from 'formik'
import { Button } from 'primereact/button'
import { CheckboxChangeEvent } from 'primereact/checkbox'
import {
  Column,
  ColumnBodyOptions,
  ColumnEditorOptions,
} from 'primereact/column'
import {
  DataTableEditingRows,
  DataTableRowEditCompleteEvent,
  DataTableRowEditEvent,
  DataTableRowEditSaveEvent,
  DataTableRowEditValidatorOptions,
  DataTableValue,
  DataTableValueArray,
} from 'primereact/datatable'
import { classNames } from 'primereact/utils'

import { InputTextToggleable } from '../../../../../../../components/inputs'
import { PriceBodyTemplate } from '../../../../../../../components/tableTemplates'
import {
  InvoiceLineType,
  RequiredTrackedValueString1,
  TrackedValueString,
  TrackedValueString1,
} from '../../../../../../../data/models/_generatedUblInvoice'
import { formatQuantity } from '../../../../../../../utils/formatUtils'
import {
  ConfigurableComponent,
  ConfigurableTable,
} from '../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../ui-config'
import { ValueStatus } from '../../../../../common/value-status'
import { FormFieldName, FormValues } from './lines.form'

type Props = {
  formik: FormikProps<FormValues>
  invoiceLines?: InvoiceLineType[]
  isLoading: boolean
  editable?: boolean
  hasLoadingRowId: (rowId: string) => boolean
  handleSave: (formData: InvoiceLineType) => Promise<void>
  isFormFieldValid: (index: number, name: FormFieldName) => boolean
  getFormErrorMessage: (
    index: number,
    name: FormFieldName,
  ) => false | JSX.Element
}

export const LinesView: React.FC<Props> = ({
  formik,
  invoiceLines,
  isLoading,
  editable = false,
  hasLoadingRowId,
  handleSave,
  isFormFieldValid,
  getFormErrorMessage,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const { uiConfig: config } = useConfigContext()

  const [editingRows, setEditingRows] = useState<DataTableEditingRows>({})

  const isEditingMode = Object.keys(editingRows).length > 0

  // Helper function to get row index by LineId
  const getRowIndex = (lineId?: string | null) => {
    if (!lineId) return -1

    return (
      formik.values.invoiceLines?.findIndex(line => line.LineId === lineId) ??
      -1
    )
  }

  // lineIdEditor
  const lineIdEditor = (options: ColumnEditorOptions) => {
    const rowIndex = getRowIndex(options.rowData.LineId)

    const handleCheckChange = (e: CheckboxChangeEvent) => {
      const value = e.checked

      formik.setFieldValue(
        `invoiceLines.${rowIndex}.OrderLineReference.LineID.IsEditEnabled`,
        value,
      )

      if (!value)
        formik.setFieldValue(
          `invoiceLines.${rowIndex}.OrderLineReference.LineID.Value`,
          formik?.values?.invoiceLines?.[rowIndex]?.OrderLineReference?.LineID
            ?.OriginalValue,
        )
    }

    const lineIdComponent = (
      <InputTextToggleable
        id="lineId"
        checked={
          formik.values.invoiceLines?.[rowIndex]?.OrderLineReference?.LineID
            ?.IsEditEnabled || false
        } // {options.value}
        onCheckedChange={handleCheckChange}
        value={
          formik.values.invoiceLines?.[rowIndex]?.OrderLineReference?.LineID
            ?.Value ?? ''
        } // {options.value}
        onChange={event => {
          const value = event.target.value || ''

          formik.setFieldValue(
            `invoiceLines.${rowIndex}.OrderLineReference.LineID.Value`,
            value,
          )
        }} // {(e: React.ChangeEvent<HTMLInputElement>) => options.editorCallback!(e.target.value)}
        autoFocus
        className={classNames('w-full', {
          'p-invalid': !isFormFieldValid(rowIndex, 'LineID' as FormFieldName),
        })}
      />
    )

    const lineIdErrorMsg = (
      <div>{getFormErrorMessage(rowIndex, 'LineID' as FormFieldName)}</div>
    )

    const lineIdWrapper = (
      <>
        <ConfigurableComponent
          key={`lineId-${rowIndex}`}
          config={config.eInvoice.additionalData.invoiceLines}
          component={lineIdComponent}
          errorMsg={lineIdErrorMsg}
          className="mb-2"
        />

        <ValueStatus
          trackedValue={invoiceLines?.[rowIndex]?.OrderLineReference?.LineID}
          formikValue={
            formik.values.invoiceLines?.[rowIndex]?.OrderLineReference?.LineID
              ?.Value
          }
        />
      </>
    )

    return <>{lineIdWrapper}</>
  }

  // buyersItemIdEditor
  const buyersItemIdEditor = (options: ColumnEditorOptions) => {
    const rowIndex = getRowIndex(options.rowData.LineId)

    const handleCheckChange = (e: CheckboxChangeEvent) => {
      const value = e.checked

      formik.setFieldValue(
        `invoiceLines.${rowIndex}.Item.BuyersItemIdentification.ID.Value.IsEditEnabled`,
        value,
      )

      if (!value)
        formik.setFieldValue(
          `invoiceLines.${rowIndex}.Item.BuyersItemIdentification.ID.Value.Value`,
          formik?.values?.invoiceLines?.[rowIndex]?.Item
            ?.BuyersItemIdentification?.ID?.Value?.OriginalValue,
        )
    }

    const buyersItemIdComponent = (
      <InputTextToggleable
        id="buyersItemId"
        checked={
          formik.values.invoiceLines?.[rowIndex]?.Item?.BuyersItemIdentification
            ?.ID?.Value?.IsEditEnabled || false
        }
        onCheckedChange={handleCheckChange}
        value={
          formik.values.invoiceLines?.[rowIndex]?.Item?.BuyersItemIdentification
            ?.ID?.Value?.Value ?? ''
        }
        onChange={event => {
          const value = event.target.value || ''

          formik.setFieldValue(
            `invoiceLines.${rowIndex}.Item.BuyersItemIdentification.ID.Value.Value`,
            value,
          )
        }}
        autoFocus
        className={classNames('w-full', {
          'p-invalid': !isFormFieldValid(
            rowIndex,
            'BuyersItemID' as FormFieldName,
          ),
        })}
      />
    )

    const buyersItemIdErrorMsg = (
      <div>
        {getFormErrorMessage(rowIndex, 'BuyersItemID' as FormFieldName)}
      </div>
    )

    const buyersItemIdWrapper = (
      <>
        <ConfigurableComponent
          key={`buyersItemId-${rowIndex}`}
          config={config.eInvoice.additionalData.invoiceLines}
          component={buyersItemIdComponent}
          errorMsg={buyersItemIdErrorMsg}
          className="mb-2"
        />

        <ValueStatus
          trackedValue={
            invoiceLines?.[rowIndex]?.Item?.BuyersItemIdentification?.ID?.Value
          }
          formikValue={
            formik.values.invoiceLines?.[rowIndex]?.Item
              ?.BuyersItemIdentification?.ID?.Value?.Value
          }
        />
      </>
    )

    return <>{buyersItemIdWrapper}</>
  }

  //  Action template
  const actionBodyTemplate = (
    rowData: InvoiceLineType,
    options: ColumnBodyOptions,
  ) => {
    if (options.rowEditor?.editing)
      return (
        <div className="flex justify-content-end gap-2">
          <Button
            icon="pi pi-times"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onCancelClick) {
                options.rowEditor.onCancelClick(event)
              }
            }}
          />

          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={event => {
              if (options.rowEditor?.onSaveClick) {
                options.rowEditor.onSaveClick(event)
              }
            }}
          />
        </div>
      )

    return (
      <div className="flex justify-content-end">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={event => {
            if (options.rowEditor?.onInitClick) {
              options.rowEditor.onInitClick(event)
            }
          }}
          loading={hasLoadingRowId(rowData.LineId!)}
          disabled={isEditingMode || !editable}
        />
      </div>
    )
  }

  const buttons = editable ? (
    <Column
      rowEditor
      key="buttons"
      body={(e, options) => actionBodyTemplate(e, options)}
      align="right"
      className="w-7rem"
    ></Column>
  ) : undefined

  const onRowEditInit = (event: DataTableRowEditEvent) => {}

  const onRowEditChange = (event: DataTableRowEditEvent) => {
    setEditingRows(event.data)
  }

  const onRowEditCancel = (event: DataTableRowEditEvent) => {
    const rowIndex = getRowIndex(event.data.LineId)

    const originalInvoiceLine = invoiceLines?.find(
      line => line.LineId === formik.values.invoiceLines?.[rowIndex].LineId,
    )

    formik.setFieldValue(`invoiceLines.${rowIndex}`, originalInvoiceLine)
  }

  const rowEditValidator = (
    data: DataTableValue,
    options: DataTableRowEditValidatorOptions<DataTableValueArray>,
  ) => {
    const rowErrors =
      Array.isArray(formik.errors) &&
      (formik.errors[options.rowIndex] as FormikErrors<InvoiceLineType>)

    const hasErrors =
      rowErrors &&
      typeof rowErrors === 'object' &&
      Object.keys(rowErrors).length > 0
    const isValid = !hasErrors

    return isValid
  }

  const onRowEditSave = (
    event: DataTableRowEditSaveEvent<DataTableValueArray>,
  ) => {}

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    const { newData } = event

    const rowIndex = getRowIndex(newData.LineId)

    formik.validateForm()

    handleSave(formik.values.invoiceLines![rowIndex] as InvoiceLineType)

    setEditingRows({})
  }

  const trackedBodyTemplate = (
    displayValue: string | null | undefined,
    formikValue: unknown,
    trackedValue?:
      | TrackedValueString
      | TrackedValueString1
      | RequiredTrackedValueString1,
  ) => (
    <div className="flex flex-column gap-1">
      <span className="m-0">{displayValue}</span>
      <ValueStatus trackedValue={trackedValue} formikValue={formikValue} />
    </div>
  )

  const LineIDTemplate = (rowData: InvoiceLineType) => {
    const rowIndex = getRowIndex(rowData.LineId)

    return trackedBodyTemplate(
      rowData?.OrderLineReference?.LineID?.Value ||
        rowData?.OrderLineReference?.LineID?.OriginalValue,
      rowIndex >= 0
        ? formik.values.invoiceLines?.[rowIndex]?.OrderLineReference?.LineID
            ?.Value
        : undefined,
      rowIndex >= 0
        ? invoiceLines?.[rowIndex]?.OrderLineReference?.LineID
        : undefined,
    )
  }

  const BuyersItemIDTemplate = (rowData: InvoiceLineType) => {
    const rowIndex = getRowIndex(rowData.LineId)

    return trackedBodyTemplate(
      rowData?.Item?.BuyersItemIdentification?.ID?.Value?.Value ||
        rowData?.Item?.BuyersItemIdentification?.ID?.Value?.OriginalValue,
      rowIndex >= 0
        ? formik.values.invoiceLines?.[rowIndex]?.Item?.BuyersItemIdentification
            ?.ID?.Value?.Value
        : undefined,
      rowIndex >= 0
        ? invoiceLines?.[rowIndex]?.Item?.BuyersItemIdentification?.ID?.Value
        : undefined,
    )
  }

  const NameAndDescriptionBodyTemplate = (rowData: InvoiceLineType) => {
    return (
      <div className="flex flex-column gap-1">
        <h4 className="m-0">{rowData?.Item?.Name?.Value}</h4>
        <p className="text-600 m-0">{rowData?.Item?.Description?.Value}</p>
      </div>
    )
  }

  const InvoicedQuantityBodyTemplate = (rowData: InvoiceLineType) => {
    const quantity = rowData?.InvoicedQuantity?.Value?.Value
    const formattedQuantity =
      quantity !== undefined && quantity !== null
        ? formatQuantity(quantity, 0, language)
        : null

    const unit = rowData?.InvoicedQuantity?.UnitCode?.Value

    return (
      <>
        {formattedQuantity}{' '}
        <span title={t(`unitCode.${unit}.description`)}>
          {t(`unitCode.${unit}.short`)} ({unit})
        </span>
      </>
    )
  }

  return (
    <ConfigurableTable
      config={config.eInvoice.additionalData.invoiceLines.table}
      data={formik.values.invoiceLines}
      isLoading={isLoading}
      stateKey=""
      buttons={buttons}
      dataKey="LineId"
      editMode="row"
      editingRows={editingRows}
      // Callbacks
      onRowEditInit={onRowEditInit}
      onRowEditChange={onRowEditChange}
      onRowEditCancel={onRowEditCancel}
      rowEditValidator={rowEditValidator}
      onRowEditSave={onRowEditSave}
      onRowEditComplete={onRowEditComplete}
      className="mt-4"
    >
      {/* BT-126 */}
      <Column
        key="ID.Value.Value"
        field="ID.Value.Value"
        header="ID"
        className="w-4rem"
      />
      {/* BT-132 */}
      <Column
        key="OrderLineReference.LineID.Value"
        field="OrderLineReference.LineID.Value"
        header="OrderLineReference.LineID.Value"
        body={LineIDTemplate}
        editor={lineIdEditor}
        className="w-10rem"
      />
      {/* BT-155 */}
      <Column
        key="Item.SellersItemIdentification.ID.Value.Value"
        field="Item.SellersItemIdentification.ID.Value.Value"
        header="Item.SellersItemIdentification.ID.Value.Value"
        className="w-10rem"
      />
      {/* BT-156 */}
      <Column
        key="Item.BuyersItemIdentification.ID.Value.Value"
        field="Item.BuyersItemIdentification.ID.Value.Value"
        header="Item.BuyersItemIdentification.ID.Value.Value"
        body={BuyersItemIDTemplate}
        editor={buyersItemIdEditor}
        className="w-10rem"
      />
      {/* BT-153 & BT-154 */}
      <Column
        key="Item.Name.Value"
        field="Item.Name.Value"
        header="Item.Name.Value"
        className="w-10rem"
        body={NameAndDescriptionBodyTemplate}
      />
      {/* BT-129 & BT-130 */}
      <Column
        key="InvoicedQuantity.Value.Value"
        field="InvoicedQuantity.Value.Value"
        header="InvoicedQuantity.Value.Value"
        align="right"
        className="w-4rem"
        body={InvoicedQuantityBodyTemplate}
      />
      {/* BT-131 */}
      <Column
        key="LineExtensionAmount.Value.Value"
        field="LineExtensionAmount.Value.Value"
        header="LineExtensionAmount.Value.Value"
        align="right"
        className="w-4rem"
        body={(rowData: InvoiceLineType) =>
          PriceBodyTemplate(rowData?.LineExtensionAmount?.Value?.Value, {
            currency: rowData?.LineExtensionAmount?.CurrencyID?.Value,
          })
        }
      />
    </ConfigurableTable>
  )
}
